import { Link, useRouter } from "@shared/client/navigation";
import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  CircleSlash,
  Check,
  Copy,
  Ellipsis,
  ExternalLink,
  LayoutGrid,
  LoaderCircle,
  Mail,
  MessageSquareText,
  Quote,
  RefreshCw,
  Route,
  Rows3,
  Search,
  Star,
  UsersRound,
} from "lucide-react";
import {
  DISMISS_REASONS,
  LEAD_STATUSES,
  contactMessage,
  leadMatchesSearch,
  legacyMessage,
  normalizeNamedContacts,
  type DismissReason,
  type Lead,
  type ContactCandidate,
  type LeadStatus,
} from "@shared/lib/leads-shared";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Progress } from "@shared/components/ui/progress";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Textarea } from "@shared/components/ui/textarea";
import { GmailIcon, GoogleIcon, LinkedInIcon } from "@shared/components/brand-icons";
import { cn } from "@shared/lib/utils";
import { LeadsCards, LeadsRichTable, type LeadsLayout } from "@/components/leads-views";
import { LeadsExportButton } from "@/components/leads-export-button";
import { LeadKeyFacts } from "@/components/lead-key-facts";
import { OrgLogo } from "@shared/components/org-logo";
import { SourceAnchor } from "@/components/source-anchor";
import { SourceFavicon } from "@shared/components/source-favicon";
import {
  gmailComposeUrl,
  mailtoUrl,
  textFragmentUrl,
} from "@shared/lib/source-links";
import {
  ContactPicker,
  ContactProfileCard,
  googleProfileSearchUrl,
  linkedinSearchUrl,
} from "@shared/components/contact";
import { OrgLinks } from "@shared/components/org-links";
import { LeadOverview, scoreLabel, SheetNavigation, type SheetNav } from "@/components/lead-overview";
import { LeadTaskAlert, useLeadTaskRunner, useLeadTaskRunning, useViewedLead } from "@/components/lead-tasks";
import { ScoreRing } from "@/components/leads-views";
import { useT } from "@shared/client/locale";

/** La présentation choisie (cartes ou tableau) : une préférence de ce navigateur. */
const LAYOUT_KEY = "hune-leads-layout";

export function readLayout(): LeadsLayout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === "table" ? "table" : "cards";
  } catch {
    return "cards";
  }
}

/** Maturité du signal : trois paliers, du plus chaud au plus froid, lus à un point de couleur. */
function readinessDot(readiness: string | null | undefined) {
  if (readiness === "Demande active") return "bg-destructive";
  if (readiness === "Besoin explicite") return "bg-warning";
  return "bg-subtle-foreground";
}

/** The stored (French) readiness, shown in the interface language. */
function Readiness({ value }: { value: string }) {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <i aria-hidden="true" className={cn("size-1.5 shrink-0 rounded-full", readinessDot(value))} />
      {t.leads.readiness[value] ?? value}
    </span>
  );
}

/** Axes of the dossier score, in display order (labels: `leads.osint.scoreAxes`). */
const OSINT_SCORE_AXES: Array<keyof NonNullable<Lead["osint_score_breakdown"]>> = [
  "pain_strength",
  "product_fit",
  "timing",
  "public_reachability",
  "evidence_quality",
];

type LeadModalTab = "overview" | "contacts" | "message" | "dossier";

type LeadPatch = {
  status?: LeadStatus;
  assigned_to?: string | null;
  favorite?: boolean;
  archived?: boolean;
  opened?: boolean;
  dismiss_reason?: DismissReason;
  dismiss_note?: string;
};

/** Étapes proposées à l'avancement, dans l'ordre. « Écarté » a son propre rite. */
const PIPELINE_STEPS = LEAD_STATUSES.filter((status) => status !== "dismissed");

/**
 * Titre de section interne à la fiche. Un seul intitulé : un surtitre au-dessus
 * de chaque titre doublait chaque section d'une étiquette sans information.
 */
function SectionHeading({ title, aside }: { title: string; aside?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="m-0 text-[14px] font-semibold">{title}</h3>
      {aside}
    </div>
  );
}

export function LeadsTable({
  leads,
  loadError,
  initialStatus = "all",
  initialLeadId = null,
  initialTab,
  linkKey = "",
  readOnly = false,
}: {
  leads: Lead[];
  loadError?: string;
  /** Espace client consulté par un admin : on lit, on ne modifie rien. */
  readOnly?: boolean;
  /** Filtre demandé par le lien d'arrivée (carte de veille, bilan de passage). */
  initialStatus?: "all" | LeadStatus;
  /** Fiche à ouvrir directement, quand on arrive depuis un rapport. */
  initialLeadId?: string | null;
  /** Onglet où ouvrir cette fiche (« Ouvrir » d'un toast « Messages prêts »). */
  initialTab?: LeadModalTab;
  /** Clé de la navigation qui a porté `initialLeadId` (`location.key`). */
  linkKey?: string;
}) {
  const router = useRouter();
  const t = useT();
  const copy = t.leads.table;
  const [now] = useState(() => Date.now());
  const [statusFilter, setStatusFilter] = useState<"all" | LeadStatus>(initialStatus);
  const [search, setSearch] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Ce qui était à lire à l'arrivée sur l'écran : ouvrir un lead ne doit pas le
  // faire sauter en bas de la liste pendant qu'on la parcourt avec ← et →.
  const [unreadAtLoad] = useState(() => new Set(leads.filter((lead) => !lead.opened_at).map((lead) => lead.id)));
  const [showArchived, setShowArchived] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(initialLeadId);
  // Un nouveau lien profond (la palette ⌘K, « Ouvrir » d'un toast, déjà sur
  // cet écran) ouvre sa fiche, même quand l'adresse désignait déjà ce lead :
  // chaque navigation a sa clé.
  const link = initialLeadId ? `${initialLeadId}:${linkKey}` : null;
  const [linkedLead, setLinkedLead] = useState(link);
  const [linkedTab, setLinkedTab] = useState(initialTab);
  if (link !== linkedLead) {
    setLinkedLead(link);
    setLinkedTab(initialTab);
    if (initialLeadId) setSelectedId(initialLeadId);
  }
  const [dismissing, setDismissing] = useState<Lead | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [layout, setLayout] = useState<LeadsLayout>(readLayout);

  const active = useMemo(() => leads.filter((lead) => !lead.archived_at), [leads]);
  const archivedCount = leads.length - active.length;

  const counts = useMemo(() => {
    const map = new Map<LeadStatus, number>();
    for (const lead of active) map.set(lead.status, (map.get(lead.status) ?? 0) + 1);
    return map;
  }, [active]);

  const unreadCount = useMemo(() => active.filter((lead) => !lead.opened_at).length, [active]);

  const newThisWeek = useMemo(
    () => active.filter((lead) => now - new Date(lead.last_seen_at).getTime() < 7 * 86_400_000).length,
    [active, now],
  );

  const visible = useMemo(() => {
    const pool = showArchived ? leads.filter((lead) => lead.archived_at) : active;
    // Les leads jamais ouverts passent devant, dans l'ordre du pipeline.
    const isFresh = (lead: Lead) => !lead.opened_at || unreadAtLoad.has(lead.id);
    const ordered = [...pool].sort((a, b) => Number(isFresh(b)) - Number(isFresh(a)));
    return ordered.filter((lead) => {
      if (favoritesOnly && !lead.favorite) return false;
      if (unreadOnly && !isFresh(lead)) return false;
      if (!showArchived && statusFilter !== "all" && lead.status !== statusFilter) return false;
      return leadMatchesSearch(lead, search);
    });
  }, [leads, active, statusFilter, search, favoritesOnly, unreadOnly, unreadAtLoad, showArchived]);

  const selected = useMemo(
    () => (selectedId ? leads.find((lead) => lead.id === selectedId) ?? null : null),
    [leads, selectedId],
  );

  async function patchLead(id: string, body: LeadPatch) {
    setError("");
    try {
      const response = await fetch(`/api/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error((data as { error?: string }).error || copy.updateFailed);
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : copy.updateFailed);
    }
  }

  // Arrivée depuis un rapport (« ?lead=… ») : la fiche s'ouvre, et elle compte
  // comme lue au même titre qu'un clic dans la table.
  useEffect(() => {
    if (!initialLeadId || readOnly) return;
    const target = leads.find((lead) => lead.id === initialLeadId);
    if (!target || target.opened_at) return;
    // Notification du serveur, pas un état de rendu : différée d'un tour pour
    // ne pas déclencher de cascade de rendus depuis le corps de l'effet.
    const timer = setTimeout(() => void patchLead(target.id, { opened: true }), 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLeadId]);

  function chooseLayout(next: LeadsLayout) {
    setLayout(next);
    try {
      localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      // Stockage indisponible : le choix vaut pour cette visite.
    }
  }

  function openLead(lead: Lead) {
    setSelectedId(lead.id);
    if (!lead.opened_at && !readOnly) patchLead(lead.id, { opened: true });
  }

  async function copyMessage(lead: Lead, message?: string) {
    const content = message?.trim() || lead.latest_signal?.message_draft?.trim();
    if (!content) return;
    await navigator.clipboard.writeText(content);
    setCopiedId(lead.id);
    setTimeout(() => setCopiedId(null), 1600);
  }

  // L'enquête survit à la fiche : son issue s'annonce par un toast (lead-tasks).
  const runTask = useLeadTaskRunner();
  function refreshLeadResearch(lead: Lead) {
    return runTask("enrich", lead);
  }

  const selectedIndex = selected ? visible.findIndex((lead) => lead.id === selected.id) : -1;
  const sheetNav: SheetNav | undefined =
    selectedIndex >= 0
      ? {
          index: selectedIndex,
          total: visible.length,
          onPrev: () => selectedIndex > 0 && openLead(visible[selectedIndex - 1]!),
          onNext: () => selectedIndex < visible.length - 1 && openLead(visible[selectedIndex + 1]!),
        }
      : undefined;

  if (loadError) {
    return (
      <>
        <div className="mb-4 flex items-center justify-between gap-4 text-[12px] text-muted-foreground">
          <span>{copy.pipeline}</span>
        </div>
        <Alert role="alert" variant="destructive">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      </>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 text-[12px] text-muted-foreground">
        <span>
          {copy.count(active.length)}
          {(unreadCount > 0 || unreadOnly) && (
            <>
              {" • "}
              <button
                aria-pressed={unreadOnly}
                className="cursor-pointer border-0 bg-transparent p-0 font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => setUnreadOnly((value) => !value)}
                title={unreadOnly ? copy.showAllTitle : copy.unreadOnlyTitle}
                type="button"
              >
                {unreadOnly ? copy.showAll : copy.unread(unreadCount)}
              </button>
            </>
          )}
          {newThisWeek > 0 && <> • {copy.signalsThisWeek(newThisWeek)}</>}
        </span>
        <span className="text-[12px] text-muted-foreground/80">{copy.fedBy}</span>
      </div>

      {error && (
        <Alert className="mb-4" role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="gap-0 overflow-hidden py-0">
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <div className="relative min-w-[220px] flex-1 max-sm:min-w-full">
            <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-9 pl-9"
              onChange={(event) => setSearch(event.target.value)}
              placeholder={copy.searchPlaceholder}
              value={search}
            />
          </div>

          {!showArchived && (
            <Tabs
              className="max-w-full overflow-x-auto [scrollbar-width:none] max-sm:-mx-4 max-sm:px-4"
              onValueChange={(value) => setStatusFilter(value as "all" | LeadStatus)}
              value={statusFilter}
            >
              <TabsList aria-label={copy.statusFilterLabel} className="h-8 w-max">
                <TabsTrigger className="px-2.5 text-[13px]" value="all">{copy.allStatuses}</TabsTrigger>
                {LEAD_STATUSES.map((status) => (
                  <TabsTrigger className="px-2.5 text-[13px]" key={status} value={status}>
                    {t.leads.statuses[status]}
                    {(counts.get(status) ?? 0) > 0 && (
                      <span className="text-[12px] tabular-nums text-muted-foreground">{counts.get(status)}</span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}

          <div className="flex items-center gap-2">
            <Tabs onValueChange={(value) => chooseLayout(value as LeadsLayout)} value={layout}>
              <TabsList aria-label={copy.layoutLabel} className="h-8">
                <TabsTrigger className="px-2" title={copy.cards} value="cards">
                  <LayoutGrid /> <span className="sr-only">{copy.cards}</span>
                </TabsTrigger>
                <TabsTrigger className="px-2" title={copy.table} value="table">
                  <Rows3 /> <span className="sr-only">{copy.table}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              aria-pressed={favoritesOnly}
              onClick={() => setFavoritesOnly((value) => !value)}
              size="sm"
              type="button"
              variant={favoritesOnly ? "secondary" : "outline"}
            >
              <Star className={cn(favoritesOnly && "fill-current")} /> {copy.favorites}
            </Button>
            {(archivedCount > 0 || showArchived) && (
              <Button
                aria-pressed={showArchived}
                onClick={() => setShowArchived((value) => !value)}
                size="sm"
                type="button"
                variant={showArchived ? "secondary" : "outline"}
              >
                <Archive /> {copy.archived}
                {archivedCount > 0 && <span className="tabular-nums text-muted-foreground">{archivedCount}</span>}
              </Button>
            )}
            <LeadsExportButton
              favoritesOnly={favoritesOnly}
              filter={showArchived ? "archived" : statusFilter}
              leads={leads}
              onError={setError}
              search={search}
            />
          </div>
        </div>

        {visible.length === 0 ? (
          <p className="m-0 px-6 py-14 text-center text-[13px] text-muted-foreground">
            {leads.length === 0 ? (
              <>
                {copy.empty.before}
                <Link className="font-semibold text-primary hover:underline" href="/app#watch">{copy.empty.link}</Link>
                {copy.empty.after}
              </>
            ) : showArchived ? (
              copy.emptyArchived
            ) : (
              copy.emptyFiltered
            )}
          </p>
        ) : layout === "table" ? (
          <LeadsRichTable leads={visible} onOpen={openLead} onPatch={patchLead} readOnly={readOnly} selectedId={selectedId} />
        ) : (
          <LeadsCards leads={visible} onOpen={openLead} onPatch={patchLead} readOnly={readOnly} selectedId={selectedId} />
        )}
      </Card>

      {selected && (
        <LeadModal
          copied={copiedId === selected.id}
          key={selected.id}
          nav={sheetNav}
          initialTab={selected.id === initialLeadId ? linkedTab : undefined}
          lead={selected}
          onClose={() => {
            setSelectedId(null);
            setLinkedTab(undefined);
          }}
          onCopy={(message) => copyMessage(selected, message)}
          onDismiss={() => setDismissing(selected)}
          onEnrich={() => refreshLeadResearch(selected)}
          onPatch={patchLead}
          readOnly={readOnly}
        />
      )}

      {dismissing && (
        <DismissDialog
          lead={dismissing}
          onClose={() => setDismissing(null)}
          onConfirm={async (reason, note) => {
            await patchLead(dismissing.id, {
              status: "dismissed",
              dismiss_reason: reason,
              dismiss_note: note || undefined,
            });
            setDismissing(null);
            setSelectedId(null);
          }}
        />
      )}
    </>
  );
}

function LeadModal({
  lead,
  nav,
  initialTab = "overview",
  copied,
  onClose,
  onCopy,
  onDismiss,
  onEnrich,
  onPatch,
  readOnly,
}: {
  lead: Lead;
  nav?: SheetNav;
  initialTab?: LeadModalTab;
  copied: boolean;
  readOnly: boolean;
  onClose: () => void;
  onCopy: (message?: string) => void;
  onDismiss: () => void;
  onEnrich: () => Promise<void>;
  onPatch: (id: string, body: LeadPatch) => Promise<void>;
}) {
  const t = useT();
  const copy = t.leads.sheet;
  const signal = lead.latest_signal;
  const isReactivated = Boolean(lead.reactivated_at) && lead.status === "new";
  const contacts = useMemo(() => normalizeNamedContacts(lead.contacts), [lead.contacts]);
  const [activeTab, setActiveTab] = useState<LeadModalTab>(initialTab);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(contacts[0]?.id ?? null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const selectedContact = contacts.find((contact) => contact.id === selectedContactId) ?? contacts[0] ?? null;
  const outreachMessage = contactMessage(signal?.message_draft, selectedContact);
  const hasApproach = Boolean(
    signal?.message_draft || contacts.some((contact) => contact.outreach_email || contact.icebreakers.length),
  );
  // Enquête et rédaction tournent hors de la fiche : la refermer ne les perd
  // pas, la rouvrir retrouve leur indicateur ; leur échec s'affiche ici tant
  // qu'elle est ouverte, par un toast sinon.
  const runTask = useLeadTaskRunner();
  const enriching = useLeadTaskRunning("enrich", lead.id);
  const writing = useLeadTaskRunning("emails", lead.id);
  useViewedLead(lead.id);

  function writeEmails() {
    return runTask("emails", lead);
  }
  const priority = signal?.priority ?? lead.osint_score;
  const tabs: Array<{ id: LeadModalTab; label: string; count?: number }> = [
    { id: "overview", label: copy.tabs.overview },
    { id: "contacts", label: copy.tabs.contacts, count: contacts.length || undefined },
    { id: "message", label: copy.tabs.message },
    { id: "dossier", label: copy.tabs.dossier, count: lead.osint_signals.length || undefined },
  ];

  // ⌘K ouvre le menu d'actions ; Échap et le clic extérieur sont pris en
  // charge par le Dialog et le DropdownMenu de shadcn.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setActionMenuOpen((open) => !open);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ← et → passent d'un lead à l'autre ; C copie le message, O ouvre la
  // source sur la phrase citée.
  const sourceUrl = signal?.source_link ? textFragmentUrl(signal.source_link, signal.source_quote) : null;
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true], [role=tablist], [role=menu]")) return;
      if (event.key === "ArrowLeft") nav?.onPrev();
      if (event.key === "ArrowRight") nav?.onNext();
      if (event.key.toLowerCase() === "c" && outreachMessage) onCopy(outreachMessage);
      if (event.key.toLowerCase() === "o" && sourceUrl) window.open(sourceUrl, "_blank", "noreferrer");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, outreachMessage, onCopy, sourceUrl]);

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open>
      <DialogContent
        // Le focus va à la fenêtre, pas à la flèche « précédent » (premier bouton).
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        className="flex max-h-[88vh] flex-col outline-none gap-0 overflow-hidden p-0 sm:max-w-5xl max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:max-w-full max-sm:rounded-none max-sm:border-0"
      >
        <DialogHeader className="flex flex-row items-start justify-between gap-6 border-b p-6 pr-16 text-left max-sm:flex-col max-sm:gap-3 max-sm:p-4 max-sm:pr-12">
          <div className="flex min-w-0 items-start gap-4">
          <OrgLogo className="mt-0.5 max-sm:hidden" name={lead.name} size={56} website={lead.website} />
          <div className="min-w-0">
            <DialogTitle className="font-serif text-[26px] leading-tight font-normal tracking-[-0.01em] max-sm:text-[22px]">{lead.name}</DialogTitle>
            <OrgLinks className="mt-2" organization={lead} />
            <DialogDescription asChild>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
                {signal?.readiness && <Readiness value={signal.readiness} />}
                {isReactivated && <span className="text-foreground">{copy.reactivated}</span>}
                {lead.category && <span>{lead.category}</span>}
                {lead.signal_count > 0 && (
                  <span>{copy.signalCount(lead.signal_count)}</span>
                )}
              </div>
            </DialogDescription>
          </div>
          </div>

          <div className="flex flex-none items-center gap-4 max-sm:w-full max-sm:justify-between">
            {/* Le score vit dans la colonne de droite ; sous lg, elle passe sous le récit. */}
            {typeof priority === "number" && (
              <span className="flex items-center gap-3 lg:hidden">
                <ScoreRing score={priority} size={44} />
                <span className="text-[13px] font-medium">{scoreLabel(priority, t.leads.overview.scoreLabels)}</span>
              </span>
            )}
            {nav && <SheetNavigation nav={nav} />}
            {!readOnly && <LeadActionMenu
              copied={copied}
              hasMessage={Boolean(outreachMessage)}
              lead={lead}
              onArchive={() => {
                onPatch(lead.id, { archived: !lead.archived_at });
                onClose();
              }}
              onCopy={() => onCopy(outreachMessage)}
              onDismiss={onDismiss}
              onEnrich={onEnrich}
              onOpenChange={setActionMenuOpen}
              onOpenMessage={() => setActiveTab("message")}
              onPatch={onPatch}
              open={actionMenuOpen}
            />}
          </div>
        </DialogHeader>

        {lead.status === "dismissed" && lead.dismiss_reason && (
          <div className="mx-6 mt-4 flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3 max-sm:mx-4">
            <CircleSlash aria-hidden="true" className="size-4 flex-none text-muted-foreground" />
            <p className="m-0 text-[12px] text-muted-foreground">
              {copy.dismissed} — <strong className="font-semibold">{t.leads.dismissReasons[lead.dismiss_reason]?.label ?? lead.dismiss_reason}</strong>
              {lead.dismiss_note && <> · {lead.dismiss_note}</>}
            </p>
            <Button asChild className="ml-auto text-[12px]" size="sm" variant="link">
              <Link href="/app/offer">{copy.fixTargeting}</Link>
            </Button>
          </div>
        )}

        <Tabs
          className="flex min-h-0 flex-1 flex-col gap-0"
          onValueChange={(value) => setActiveTab(value as LeadModalTab)}
          value={activeTab}
        >
          <TabsList aria-label={copy.tabsLabel} className="mx-6 mt-4 w-fit max-sm:mx-4 max-sm:max-w-[calc(100%-2rem)] max-sm:overflow-x-auto max-sm:justify-start">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
                {typeof tab.count === "number" && (
                  <span className="text-[12px] tabular-nums text-muted-foreground">{tab.count}</span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <ScrollArea className="min-h-0 flex-1">
            <div className="p-6 max-sm:p-4">
              <LeadTaskAlert className="mb-4" kind="enrich" leadId={lead.id} onRetry={readOnly ? undefined : onEnrich} />

              <TabsContent value="overview">
                <LeadOverview
                  contacts={contacts}
                  lead={lead}
                  onOpenContact={(id) => {
                    setSelectedContactId(id);
                    setActiveTab("contacts");
                  }}
                  onStatus={readOnly ? undefined : (status) => onPatch(lead.id, { status })}
                />
              </TabsContent>

              <TabsContent value="contacts">
                <ContactWorkspace
                  contacts={contacts}
                  lead={lead}
                  onSelect={setSelectedContactId}
                  selectedContact={selectedContact}
                />
              </TabsContent>

              <TabsContent value="message">
                <MessageWorkspace
                  contacts={contacts}
                  lead={lead}
                  legacy={legacyMessage(signal?.message_draft, selectedContact)}
                  onSelect={setSelectedContactId}
                  onWrite={readOnly ? undefined : writeEmails}
                  selectedContact={selectedContact}
                  writing={writing}
                />
              </TabsContent>

              <TabsContent value="dossier">
                {lead.osint_researched_at ? (
                  <OsintDossier enriching={enriching} lead={lead} onEnrich={readOnly ? undefined : onEnrich} />
                ) : readOnly ? (
                  <p className="m-0 text-[13px] text-muted-foreground">{copy.noResearch}</p>
                ) : (
                  <OsintStarter enriching={enriching} onEnrich={onEnrich} />
                )}
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>

        {/* Sur l'onglet Message, chaque partie de l'e-mail a sa propre copie. */}
        {hasApproach && activeTab !== "message" && (
          <DialogFooter className="border-t bg-muted/30 px-6 py-3.5 max-sm:px-4 max-sm:pb-[max(env(safe-area-inset-bottom),0.875rem)]">
            <Button onClick={() => setActiveTab("message")} type="button">
              <MessageSquareText /> {copy.prepareApproach}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LeadActionMenu({
  lead,
  copied,
  hasMessage,
  open,
  onOpenChange,
  onOpenMessage,
  onCopy,
  onEnrich,
  onPatch,
  onArchive,
  onDismiss,
}: {
  lead: Lead;
  copied: boolean;
  hasMessage: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenMessage: () => void;
  onCopy: () => void;
  onEnrich: () => Promise<void>;
  onPatch: (id: string, body: LeadPatch) => Promise<void>;
  onArchive: () => void;
  onDismiss: () => void;
}) {
  const t = useT();
  const copy = t.leads.actionMenu;
  return (
    <DropdownMenu onOpenChange={onOpenChange} open={open}>
      <DropdownMenuTrigger asChild>
        <Button aria-label={copy.open} size="icon" title={copy.title} type="button" variant="outline">
          <Ellipsis />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="flex items-center justify-between">
          {copy.label}
          <DropdownMenuShortcut>⌘ K</DropdownMenuShortcut>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hasMessage && (
          <>
            <DropdownMenuItem onSelect={onOpenMessage}><MessageSquareText /> {t.leads.sheet.prepareApproach}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onCopy}>
              <Copy /> {copied ? copy.messageCopied : copy.copyMessage}
              <DropdownMenuShortcut>C</DropdownMenuShortcut>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem onSelect={() => void onEnrich()}><RefreshCw /> {copy.refreshResearch}</DropdownMenuItem>
        {lead.latest_signal?.source_link && (
          <DropdownMenuItem asChild>
            <a href={textFragmentUrl(lead.latest_signal.source_link, lead.latest_signal.source_quote)} rel="noreferrer" target="_blank">
              <ExternalLink /> {copy.openSource}
              <DropdownMenuShortcut>O</DropdownMenuShortcut>
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {/* Le statut faisait tourner tout le pipeline (filtres, compteurs,
            mémoire de l'agent) sans qu'aucun écran ne permette d'en changer.
            Il se pilote ici, à côté des actions de la fiche. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger><Route /> {copy.advance}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {PIPELINE_STEPS.map((status) => (
              <DropdownMenuItem
                disabled={lead.status === status}
                key={status}
                onSelect={() => void onPatch(lead.id, { status })}
              >
                {lead.status === status ? <Check /> : <span className="size-4" aria-hidden="true" />}
                {t.leads.statuses[status]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={onDismiss} variant="destructive">
          <CircleSlash /> {copy.dismiss}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void onPatch(lead.id, { favorite: !lead.favorite })}>
          <Star /> {lead.favorite ? t.leads.actions.removeFavorite : t.leads.actions.addFavorite}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onArchive}>
          {lead.archived_at ? <ArchiveRestore /> : <Archive />} {lead.archived_at ? t.leads.actions.restore : t.leads.actions.archive}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Barre de qualification : une mesure bornée, donc une jauge, jamais un graphique. */
function LinearScore({ label, value, maximum = 5 }: { label: string; value: number; maximum?: number }) {
  const t = useT();
  const percent = Math.max(0, Math.min(100, (value / maximum) * 100));
  return (
    <div className="grid gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] text-muted-foreground">{label}</span>
        <small className="text-[12.5px] font-medium tabular-nums">{value}/{maximum}</small>
      </div>
      <Progress
        aria-label={t.leads.linearScoreAria(label, value, maximum)}
        className="h-1 [&>[data-slot=progress-indicator]]:bg-foreground/80"
        value={percent}
      />
    </div>
  );
}

function ContactWorkspace({
  lead,
  contacts,
  selectedContact,
  onSelect,
}: {
  lead: Lead;
  contacts: ContactCandidate[];
  selectedContact: ContactCandidate | null;
  onSelect: (id: string) => void;
}) {
  const copy = useT().leads.contacts;
  if (contacts.length === 0) {
    return (
      <div className="grid justify-items-center gap-3 py-8 text-center">
        <UsersRound className="size-7 text-muted-foreground" />
        <h3 className="m-0 text-[15px] font-semibold">{copy.emptyTitle}</h3>
        <p className="m-0 max-w-[520px] text-[12.5px] text-muted-foreground">{copy.emptyText}</p>
        <LegacyContact lead={lead} />
      </div>
    );
  }
  return (
    <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      <section aria-label={copy.pickerLabel}>
        <SectionHeading
          aside={<small className="text-[12px] text-muted-foreground">{copy.profiles(contacts.length)}</small>}
          title={copy.committee}
        />
        <ContactPicker contacts={contacts} onSelect={onSelect} selectedId={selectedContact?.id ?? null} />
        {lead.contact_research_context && (
          <p className="mt-3 mb-0 text-[12px] leading-relaxed text-muted-foreground">{lead.contact_research_context}</p>
        )}
        {lead.contact_research_gaps && lead.contact_research_gaps.length > 0 && (
          <details className="mt-3 rounded-lg border p-3 text-[12px]">
            <summary className="cursor-pointer font-semibold">
              {copy.gaps(lead.contact_research_gaps.length)}
            </summary>
            <ul className="mt-2 mb-0 grid gap-1 pl-4 text-muted-foreground">
              {lead.contact_research_gaps.map((gap) => <li key={gap}>{gap}</li>)}
            </ul>
          </details>
        )}
      </section>
      {selectedContact && <ContactProfile contact={selectedContact} lead={lead} />}
    </div>
  );
}

function ContactProfile({ lead, contact }: { lead: Lead; contact: ContactCandidate }) {
  const copy = useT().leads.contacts;
  const proofCount = contact.sources.length + contact.public_profile_links.length + contact.personalization_facts.length;
  return (
    <ContactProfileCard contact={contact} organization={lead}>
      {contact.icebreakers[0] && (
        <blockquote className="m-0 rounded-lg bg-muted/60 p-4">
          <Quote className="size-4 text-muted-foreground" />
          <p className="mt-2 mb-2 text-[12.5px] leading-relaxed">{contact.icebreakers[0].text}</p>
          <SourceAnchor href={contact.icebreakers[0].source_url} />
        </blockquote>
      )}

      <details className="rounded-lg border p-3 text-[12px]">
        <summary className="flex cursor-pointer items-center justify-between font-semibold">
          {copy.fullDossier}
          <span className="text-[12px] font-normal text-muted-foreground">{copy.proofs(proofCount)}</span>
        </summary>
        <ContactDeepDetails contact={contact} />
      </details>
    </ContactProfileCard>
  );
}

/** Copie en un clic, discrète : l'icône devient une coche le temps de le confirmer. */
function CopyButton({ text, label }: { text: string; label: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      aria-label={copied ? t.leads.message.copied : label}
      className="size-7 flex-none text-muted-foreground"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        });
      }}
      size="icon"
      title={copied ? t.leads.message.copied : label}
      type="button"
      variant="ghost"
    >
      {copied ? <Check /> : <Copy />}
    </Button>
  );
}

function EmailBlock({
  subject,
  body,
  label,
  actions,
}: {
  subject?: string;
  body: string;
  label: string;
  /** Ouvrir le brouillon ailleurs (Gmail, LinkedIn) : sous le message, là où l'œil finit. */
  actions?: React.ReactNode;
}) {
  const copy = useT().leads.message;
  return (
    <article className="min-w-0 rounded-lg border">
      {subject && (
        <div className="flex items-center gap-3 border-b py-1.5 pr-1.5 pl-4">
          <span className="text-[12px] text-muted-foreground">{copy.subject}</span>
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{subject}</span>
          <CopyButton label={copy.copySubject} text={subject} />
        </div>
      )}
      <div className="flex items-start gap-3 py-3 pr-1.5 pl-4">
        <p className="m-0 min-w-0 flex-1 py-0.5 text-[13.5px] leading-relaxed whitespace-pre-line">{body}</p>
        <CopyButton label={label} text={body} />
      </div>
      {actions && <div className="flex flex-wrap gap-2 border-t px-4 py-3">{actions}</div>}
    </article>
  );
}

/**
 * Le brouillon ouvert déjà rempli : Gmail dans un onglet, ou la messagerie du
 * poste par `mailto:`. On évite le va-et-vient copier, ouvrir, coller deux fois.
 */
function ComposeActions({ to, subject, body }: { to: string | null; subject: string; body: string }) {
  const copy = useT().leads.message;
  return (
    <>
      <Button asChild size="sm">
        <a href={gmailComposeUrl({ to, subject, body })} rel="noreferrer" target="_blank">
          <span className="grid size-4 place-items-center rounded-[3px] bg-background"><GmailIcon className="size-3" /></span>
          {copy.openInGmail}
        </a>
      </Button>
      <Button asChild size="sm" variant="outline">
        <a href={mailtoUrl({ to, subject, body })}>
          <Mail /> {copy.otherMail}
        </a>
      </Button>
    </>
  );
}

/** LinkedIn n'accepte pas de message prérempli : on le copie, puis on ouvre le profil. */
function LinkedInHandoff({ href, body, name }: { href: string; body: string; name: string }) {
  const copy = useT().leads.message;
  const [copied, setCopied] = useState(false);
  return (
    <>
      <Button asChild size="sm" variant="outline">
        <a
          href={href}
          onClick={() => {
            void navigator.clipboard.writeText(body).then(() => setCopied(true));
          }}
          rel="noreferrer"
          target="_blank"
        >
          <LinkedInIcon /> {copy.linkedinHandoff(name)}
        </a>
      </Button>
      {copied && (
        <span className="self-center text-[12px] text-muted-foreground">{copy.linkedinCopied}</span>
      )}
    </>
  );
}

function MessageWorkspace({
  lead,
  contacts,
  selectedContact,
  legacy,
  writing,
  onSelect,
  onWrite,
}: {
  lead: Lead;
  contacts: ContactCandidate[];
  selectedContact: ContactCandidate | null;
  /** Message d'avant les e-mails par contact, affiché tant qu'ils ne sont pas rédigés. */
  legacy: string;
  writing: boolean;
  onSelect: (id: string) => void;
  onWrite?: () => Promise<void>;
}) {
  const copy = useT().leads.message;
  const email = selectedContact?.outreach_email ?? null;
  const writeButton = onWrite && contacts.length > 0 && (
    <Button disabled={writing} onClick={() => void onWrite()} size="sm" type="button" variant={email ? "ghost" : "outline"}>
      {writing ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
      {writing ? copy.writing : email ? copy.rewrite : copy.writePerContact}
    </Button>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-[0.7fr_1.3fr]">
      <aside>
        <span className="text-[12px] font-medium text-muted-foreground">{copy.recipient}</span>
        <div className="mt-3">
          <ContactPicker contacts={contacts} onSelect={onSelect} selectedId={selectedContact?.id ?? null} showLane={false} />
        </div>
      </aside>

      <section className="min-w-0">
        <SectionHeading
          aside={email && (
            <span className="text-[12px] text-muted-foreground tabular-nums">{copy.wordCount(email.word_count)}</span>
          )}
          title={selectedContact ? copy.writeTo(selectedContact.contact_name) : copy.approachMessage}
        />
        <LeadTaskAlert className="mb-3" kind="emails" leadId={lead.id} onRetry={onWrite} />

        {email ? (
          <div className="grid gap-4">
            <EmailBlock
              actions={<ComposeActions body={email.body} subject={email.subject} to={selectedContact?.public_professional_email ?? null} />}
              body={email.body}
              label={copy.copyEmail}
              subject={email.subject}
            />
            {email.follow_up && (
              <div>
                <p className="m-0 mb-2 text-[12px] text-muted-foreground">{copy.followUpHint}</p>
                <EmailBlock body={email.follow_up} label={copy.copyFollowUp} />
              </div>
            )}
            {email.linkedin && (
              <div>
                <p className="m-0 mb-2 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <LinkedInIcon className="size-3.5" /> {copy.linkedinHint}
                </p>
                <EmailBlock
                  actions={selectedContact && (
                    <LinkedInHandoff
                      body={email.linkedin}
                      href={selectedContact.contact_linkedin_url || linkedinSearchUrl(lead, selectedContact)}
                      name={selectedContact.contact_name.split(/\s+/)[0] ?? selectedContact.contact_name}
                    />
                  )}
                  body={email.linkedin}
                  label={copy.copyLinkedin}
                />
              </div>
            )}
            {(email.angle || email.sources.length > 0) && (
              <div className="rounded-lg bg-muted/50 p-4 text-[12px]">
                <strong className="font-semibold">{copy.whyThisEmail}</strong>
                {email.angle && <p className="m-0 mt-1.5 leading-relaxed text-muted-foreground">{email.angle}</p>}
                {email.sources.length > 0 && (
                  <ul className="mt-2 mb-0 grid gap-1 pl-4">
                    {email.sources.map((url) => (
                      <li key={url}>
                        <SourceAnchor href={url} iconSize={14} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end">{writeButton}</div>
          </div>
        ) : legacy ? (
          <div className="grid gap-3">
            <EmailBlock
              actions={<ComposeActions body={legacy} subject="" to={selectedContact?.public_professional_email ?? null} />}
              body={legacy}
              label={copy.copyMessage}
            />
            <div className="flex items-center justify-between gap-3 max-sm:flex-col max-sm:items-start">
              <p className="m-0 text-[12px] text-muted-foreground">{copy.legacyNote}</p>
              {writeButton}
            </div>
          </div>
        ) : (
          <div className="grid justify-items-start gap-3">
            <p className="m-0 text-[12.5px] text-muted-foreground">
              {contacts.length > 0 ? copy.noEmailYet : copy.noContact(lead.name)}
            </p>
            {writeButton}
          </div>
        )}
      </section>
    </div>
  );
}

function ContactDeepDetails({ contact }: { contact: ContactCandidate }) {
  const copy = useT().leads.contactDetails;
  return (
    <div className="mt-3 grid gap-4 text-[12px] [&_h4]:mb-1.5 [&_h4]:text-[12px] [&_h4]:font-semibold [&_li]:mb-1 [&_p]:m-0 [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_ul]:m-0 [&_ul]:grid [&_ul]:gap-1 [&_ul]:pl-4 [&_ul]:text-muted-foreground">
      {contact.professional_summary && (
        <div><h4>{copy.summary}</h4><p>{contact.professional_summary}</p></div>
      )}
      {contact.responsibilities.length > 0 && <ContactInfoList items={contact.responsibilities} title={copy.responsibilities} />}
      {contact.relevant_background.length > 0 && <ContactInfoList items={contact.relevant_background} title={copy.background} />}
      {contact.recent_professional_signals.length > 0 && (
        <div>
          <h4>{copy.recentSignals}</h4>
          <ul>
            {contact.recent_professional_signals.map((item, index) => (
              <li key={`${item.source_url}-${index}`}>
                {item.fact}{" "}
                <SourceAnchor ariaLabel={copy.source} className="inline-flex align-[-2px]" href={item.source_url}><SourceFavicon size={14} url={item.source_url} /></SourceAnchor>
              </li>
            ))}
          </ul>
        </div>
      )}
      {contact.personalization_facts.length > 0 && (
        <div>
          <h4>{copy.personalization}</h4>
          <ul>
            {contact.personalization_facts.map((item, index) => (
              <li key={`${item.source_url}-${index}`}>
                <strong className="text-foreground">{item.fact}</strong> <span>{item.why_relevant}</span>{" "}
                <SourceAnchor className="align-[-2px]" href={item.source_url} iconSize={14} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {contact.public_profile_links.length > 0 && (
        <div>
          <h4>{copy.publicPresence}</h4>
          <ul>
            {contact.public_profile_links.map((profile) => (
              <li key={profile.url}>
                <SourceAnchor href={profile.url} iconSize={14} label={`${copy.profileKinds[profile.kind]} · ${profile.title}`} meta={profile.date} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {contact.icebreakers.length > 1 && (
        <div>
          <h4>{copy.otherIcebreakers}</h4>
          <ul>
            {contact.icebreakers.slice(1).map((item, index) => (
              <li key={`${item.source_url}-${index}`}>
                {item.text}{" "}
                <SourceAnchor ariaLabel={copy.source} className="inline-flex align-[-2px]" href={item.source_url}><SourceFavicon size={14} url={item.source_url} /></SourceAnchor>
              </li>
            ))}
          </ul>
        </div>
      )}
      {contact.sources.length > 0 && (
        <div>
          <h4>{copy.sources}</h4>
          <ul>
            {contact.sources.map((source, index) => (
              <li key={`${source.url}-${index}`}>
                <SourceAnchor href={source.url} iconSize={14} label={source.title} meta={source.date} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {contact.caveats.length > 0 && <ContactInfoList items={contact.caveats} title={copy.caveats} />}
      {contact.search_queries.length > 0 && (
        <details>
          <summary className="cursor-pointer font-semibold">{copy.searches(contact.search_queries.length)}</summary>
          <ul className="mt-2">{contact.search_queries.map((query) => <li key={query}>{query}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

function OsintStarter({
  enriching,
  onEnrich,
}: {
  enriching: boolean;
  onEnrich: () => Promise<void>;
}) {
  const copy = useT().leads.osint;
  return (
    <Card aria-busy={enriching} className="items-start gap-4 p-6">
      <div>
        <span className="text-[12px] font-medium text-muted-foreground">{copy.starterEyebrow}</span>
        <h3 className="mt-2 mb-2 text-[17px] font-semibold tracking-tight">{copy.starterTitle}</h3>
        <p className="m-0 max-w-[640px] text-[13px] leading-relaxed text-muted-foreground">{copy.starterText}</p>
      </div>
      <Button disabled={enriching} onClick={onEnrich} type="button">
        {enriching ? <LoaderCircle className="animate-spin" /> : <Search />}
        {enriching ? copy.running : copy.start}
      </Button>
    </Card>
  );
}

function OsintDossier({
  lead,
  enriching,
  onEnrich,
}: {
  lead: Lead;
  enriching: boolean;
  /** Absent en lecture seule : pas de bouton « Actualiser ». */
  onEnrich?: () => Promise<void>;
}) {
  const copy = useT().leads.osint;
  const [copiedQuestion, setCopiedQuestion] = useState<number | null>(null);

  async function copyQuestion(index: number, question: string) {
    await navigator.clipboard.writeText(question);
    setCopiedQuestion(index);
    setTimeout(() => setCopiedQuestion(null), 1600);
  }

  return (
    <section aria-busy={enriching} className="grid gap-6">
      <SectionHeading
        aside={
          <div className="flex items-center gap-2">
            {lead.osint_stage && (
              <Badge className="text-[12px]" variant="secondary">{copy.stages[lead.osint_stage]}</Badge>
            )}
            {onEnrich && <Button disabled={enriching} onClick={onEnrich} size="sm" type="button" variant="outline">
              {enriching ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
              {enriching ? copy.refreshing : copy.refresh}
            </Button>}
          </div>
        }
        title={copy.title}
      />

      <LeadKeyFacts facts={lead.osint_key_facts ?? []} />

      {(lead.osint_summary || lead.osint_score_breakdown) && (
        <div className="grid gap-5 lg:grid-cols-[1.3fr_0.7fr]">
          {lead.osint_summary && (
            <div>
              <strong className="text-[12px] font-semibold">{copy.summary}</strong>
              <p className="m-0 mt-1.5 text-[13px] leading-relaxed">{lead.osint_summary}</p>
            </div>
          )}
          {lead.osint_score_breakdown && (
            <div className="grid gap-3">
              {OSINT_SCORE_AXES.map((key) => {
                const value = lead.osint_score_breakdown?.[key] ?? 0;
                return <LinearScore key={key} label={copy.scoreAxes[key]} value={value} />;
              })}
            </div>
          )}
        </div>
      )}

      {lead.osint_signals.length > 0 ? (
        <div className="grid gap-3">
          {lead.osint_signals.map((signal, index) => (
            <article className="flex gap-3 rounded-lg border p-4" key={`${signal.source_url}-${index}`}>
              <span aria-hidden="true" className="mt-1.5 size-1.5 flex-none rounded-full bg-border-strong" />
              <div className="min-w-0">
                <header className="flex flex-wrap items-center gap-2">
                  <Badge className="text-[12px]" variant="outline">{copy.signalTypes[signal.type] ?? signal.type}</Badge>
                  {signal.date && <time className="text-[12px] text-muted-foreground">{signal.date}</time>}
                </header>
                <h4 className="mt-2 mb-1 text-[13.5px] font-semibold">{signal.title}</h4>
                <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">{signal.fact}</p>
                <p className="m-0 mt-2 text-[12.5px] leading-relaxed">
                  <strong className="mr-1.5 font-semibold">{copy.takeaway}</strong>
                  {signal.commercial_implication}
                </p>
                <SourceAnchor className="mt-2" href={signal.source_url} meta={signal.date} />
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="m-0 text-[12.5px] text-muted-foreground">{copy.noSignals}</p>
      )}

      {lead.osint_questions.length > 0 && (
        <div>
          <SectionHeading title={copy.questions} />
          <ol className="m-0 grid list-none gap-3 p-0">
            {lead.osint_questions.map((item, index) => (
              <li className="rounded-lg border p-4" key={`${item.source_url}-${index}`}>
                <p className="m-0 text-[13px] font-semibold">{item.question}</p>
                <span className="mt-1 block text-[12px] text-muted-foreground">{item.rationale}</span>
                <div className="mt-2.5 flex items-center gap-3">
                  <SourceAnchor href={item.source_url} />
                  <Button onClick={() => copyQuestion(index, item.question)} size="sm" type="button" variant="ghost">
                    {copiedQuestion === index ? <><Check /> {copy.copied}</> : <><Copy /> {copy.copy}</>}
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {(lead.osint_gaps.length > 0 || lead.osint_search_queries.length > 0) && (
        <details className="rounded-lg border p-4 text-[12px]">
          <summary className="cursor-pointer font-semibold">{copy.researchWork}</summary>
          <div className="mt-3 grid gap-4">
            {lead.osint_gaps.length > 0 && (
              <div>
                <strong className="text-[12px] font-semibold">{copy.gaps}</strong>
                <ul className="mt-1.5 mb-0 grid gap-1 pl-4 text-muted-foreground">
                  {lead.osint_gaps.map((gap) => <li key={gap}>{gap}</li>)}
                </ul>
              </div>
            )}
            {lead.osint_search_queries.length > 0 && (
              <div>
                <strong className="text-[12px] font-semibold">{copy.queries}</strong>
                <ul className="mt-1.5 mb-0 grid gap-1 pl-4 text-muted-foreground">
                  {lead.osint_search_queries.map((query) => <li key={query}>{query}</li>)}
                </ul>
              </div>
            )}
          </div>
        </details>
      )}
    </section>
  );
}

function LegacyContact({ lead }: { lead: Lead }) {
  const copy = useT().leads.legacyContact;
  if (!lead.contact_name && !lead.contact_role && !lead.contact_linkedin_url && !lead.contact_reason) return null;
  return (
    <Card className="mt-2 w-full max-w-[560px] gap-3 p-4 text-left">
      {lead.contact_linkedin_url ? (
        <Button asChild className="justify-start" variant="outline">
          <a href={lead.contact_linkedin_url} rel="noreferrer" target="_blank">
            <LinkedInIcon />
            <span className="grid text-left">
              {lead.contact_name || copy.fallbackName}
              {lead.contact_role && <small className="text-[12px] text-muted-foreground">{lead.contact_role}</small>}
            </span>
            <ExternalLink className="ml-auto" />
          </a>
        </Button>
      ) : (
        <div className="grid gap-2">
          <Button asChild className="justify-start" variant="outline">
            <a href={linkedinSearchUrl(lead)} rel="noreferrer" target="_blank">
              <LinkedInIcon />
              {copy.searchOnLinkedin(lead.contact_name || (lead.contact_role ? copy.quotedRole(lead.contact_role) : copy.theContact))}
              <ExternalLink className="ml-auto" />
            </a>
          </Button>
          <Button asChild className="justify-start" variant="outline">
            <a href={googleProfileSearchUrl(lead)} rel="noreferrer" target="_blank">
              <GoogleIcon />
              {copy.searchOnGoogle}
              <ExternalLink className="ml-auto" />
            </a>
          </Button>
        </div>
      )}
      {(lead.contact_reason || (lead.contact_sources?.length ?? 0) > 0) && (
        <div className="text-[12px]">
          {lead.contact_reason && <p className="m-0 leading-relaxed text-muted-foreground">{lead.contact_reason}</p>}
          {lead.contact_sources && lead.contact_sources.length > 0 && (
            <ul className="mt-2 mb-0 grid gap-1 pl-4">
              {lead.contact_sources.map((source, index) => (
                <li key={`${source.url}-${index}`}>
                  <SourceAnchor href={source.url} iconSize={14} label={source.title} meta={source.date} />
                  {source.date && <small className="ml-1.5 text-muted-foreground">{source.date}</small>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function ContactInfoList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h4>{title}</h4>
      <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
    </div>
  );
}

/**
 * Écarter un lead, en disant pourquoi.
 *
 * Le statut « écarté » existait déjà mais ne racontait rien : l'agent
 * reproposait des profils du même type au passage suivant. La raison est
 * choisie dans une liste fermée — seule une liste fermée s'agrège — et celles
 * qui parlent de ciblage remontent dans la mémoire de l'agent (lib/memory.ts)
 * comme exclusions.
 */
function DismissDialog({
  lead,
  onClose,
  onConfirm,
}: {
  lead: Lead;
  onClose: () => void;
  onConfirm: (reason: DismissReason, note: string) => Promise<void>;
}) {
  const t = useT();
  const copy = t.leads.dismiss;
  const [reason, setReason] = useState<DismissReason | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const selected = DISMISS_REASONS.find((item) => item.key === reason);

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="text-[17px]">{copy.title(lead.name)}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 sm:grid-cols-2">
          {DISMISS_REASONS.map((item) => (
            <button
              className={cn(
                "rounded-lg border px-3.5 py-3 text-left hover:bg-muted/70",
                reason === item.key && "border-foreground/40 bg-muted",
              )}
              key={item.key}
              onClick={() => setReason(item.key)}
              type="button"
            >
              <strong className="block text-[13px] font-semibold tracking-tight">{t.leads.dismissReasons[item.key].label}</strong>
              <small className="block text-[12px] text-muted-foreground">{t.leads.dismissReasons[item.key].hint}</small>
            </button>
          ))}
        </div>

        <div className="grid gap-2">
          <Label className="text-[12px] font-semibold text-muted-foreground" htmlFor="dismiss-note">
            {copy.noteLabel}
          </Label>
          <Textarea
            id="dismiss-note"
            onChange={(event) => setNote(event.target.value)}
            placeholder={copy.notePlaceholder}
            rows={2}
            value={note}
          />
        </div>

        {selected?.feedsIcp && (
          <p className="m-0 text-[12px] text-muted-foreground">{copy.feedsIcp}</p>
        )}

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="ghost">
            {copy.cancel}
          </Button>
          <Button
            disabled={!reason || busy}
            onClick={async () => {
              if (!reason) return;
              setBusy(true);
              try {
                await onConfirm(reason, note.trim());
              } finally {
                setBusy(false);
              }
            }}
            type="button"
            variant="destructive"
          >
            {busy ? <LoaderCircle className="animate-spin" /> : <CircleSlash />} {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
